import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "shared/@forms/Form";
import { FormActionBar } from "shared/@forms/FormActionBar";
import TextArea from "shared/@forms/TextArea";
import TextInput from "shared/@forms/TextInput";
import Button, { ButtonPurpose } from "shared/Button";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import ImagePicker from "../ImagePicker";
import styles from "./SSFCategoryEdit.module.css";
import { TwitterPicker } from "react-color";
import classNames from "classnames";
import SSFCategoryBanner from "../SSFCategoryBanner";

const defaultCategory = {
  title: "",
  subTitle: "",
  textColor: "#ffffff",
  backgroundColor: "#415364",
  isPublished: false,
  iconPath: null,
  stepNumber: 1,
  _pendingIconUpload: null,
};

/**
 * Interactive component. Provides an edit form for SSF categories.
 */
export function SSFCategoryEdit({ category, onSubmit, onCancel, showPreview }) {
  const [_category, _setCategory] = useState(defaultCategory);

  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (_category._pendingIconUpload) {
      const url = URL.createObjectURL(_category._pendingIconUpload);

      setImagePreview(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [_category._pendingIconUpload]);

  // Update if category is changed from the outside.
  useEffect(() => {
    if (category) {
      _setCategory(category);
    }
  }, [category]);

  const patchCategory = useCallback(
    (patch) => {
      _setCategory(Object.assign({}, _category, patch));
    },
    [_category]
  );

  return (
    <div className={styles.container}>
      {showPreview && (
        <div className={styles.preview}>
          <SSFCategoryBanner
            iconSrc={imagePreview || _category.iconPath}
            title={_category.title}
            subtitle={_category.subTitle}
            stepNumber={_category.stepNumber}
            textColor={_category.textColor}
            fillColor={_category.backgroundColor}
          />
        </div>
      )}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(_category);
        }}
      >
        <div className={styles.content}>
          <div className={styles.contentGroup}>
            <ImagePicker
              label="Edit"
              defaultValue={_category.iconPath}
              value={_category._pendingIconUpload}
              backgroundColor={_category.backgroundColor}
              onChange={(file) => patchCategory({ _pendingIconUpload: file })}
            />
          </div>

          <div className={classNames(styles.contentGroup, styles.textContent)}>
            <div className={styles.layoutGroup}>
              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-title">Framework Title</label>
                <TextInput
                  id="category-edit-title"
                  value={_category.title}
                  placeholder="Framework Title"
                  required={true}
                  onChange={(e) => patchCategory({ title: e.target.value })}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-subtitle">
                  Framework Subtitle
                </label>
                <TextArea
                  id="category-edit-subtitle"
                  value={_category.subTitle}
                  style={{ minHeight: "120px" }}
                  onChange={(e) => patchCategory({ subTitle: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className={styles.contentGroup}>
            <div className={styles.layoutGroup}>
              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-bg-color">Background Color</label>
                <TwitterPicker
                  id="category-edit-bg-color"
                  onChange={(e) => patchCategory({ backgroundColor: e.hex })}
                  color={_category.backgroundColor}
                  triangle="hide"
                  width="350px"
                  colors={[
                    "#000000",
                    "#243746",
                    "#415364",
                    "#7f6a45",
                    "#9e703c",
                    "#91B6BB",
                    "#a89c96",
                    "#ADB7C0",
                    "#F5891E",
                    "#DB18C4",
                    "#5CD360",
                    "#2D8EF5",
                    "#FFFFFF",
                  ]}
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="category-edit-text-color">Text Color</label>

                <TwitterPicker
                  id="category-edit-text-color"
                  onChange={(e) => patchCategory({ textColor: e.hex })}
                  color={_category.textColor}
                  triangle="hide"
                  width="350px"
                  colors={["#000000", "#FFFFFF"]}
                />
              </div>
            </div>
          </div>
        </div>

        <FormActionBar>
          <CheckboxGroup
            checked={_category.isPublished == false}
            onChange={() => {
              patchCategory({
                isPublished: !_category.isPublished,
              });
            }}
          >
            Save as Draft
          </CheckboxGroup>

          <Button
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button purpose={ButtonPurpose.save} type="submit">
            Done
          </Button>
        </FormActionBar>
      </Form>
    </div>
  );
}

SSFCategoryEdit.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
  }),

  onSubmit: PropTypes.func.isRequired,
};
